import { api } from './client'

/**
 * `GET /edge_bridge/my-active-kit` — qué kit tiene reclamado (como dueño) el
 * jugador ahora mismo. `rpi_id`/`kit_id` son `null` si no tiene ninguno
 * reclamado (sin sesión activa con el kit puesto).
 */
export interface MyActiveKit {
  rpi_id: string | null
  kit_id: string | null
}

export function getMyActiveKit(): Promise<MyActiveKit> {
  return api.get<MyActiveKit>('/edge_bridge/my-active-kit')
}

/** Build the Quest WS URL, same-origin (mismo patrón que
 * `vr-app/src/api/edgeBridge.ts::buildQuestWsUrl` — KNOW es el web-app, así
 * que la cookie de sesión viaja sola en el handshake, sin necesidad de
 * `getAuthToken()`). */
export function buildQuestWsUrl(deviceId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/edge_bridge/ws/quest/${encodeURIComponent(deviceId)}`
}

// ---------------------------------------------------------------------------
// Histórico — GET /know/me/physiology/sessions
// ---------------------------------------------------------------------------

/** Un ciclo de regulación tal cual lo escribe
 * `backend/app/services/regulation_metrics.py::compute_regulation_cycles`
 * (silver_regulation_cycles.jsonl.gz, schema 1.0.0). */
export interface RegulationCycle {
  trial_id: string
  cycle_start_ms: number
  peak_end_ms: number
  recovery_end_ms: number
  marker_basis: string
  r_cardio: number | null
  r_cardio_eda: number | null
  quality: {
    cardio: string
    cardio_eda: string
    /** Sólo presente cuando ambos índices son `null` (ciclo sin datos
     * suficientes). */
    reason?: string
  }
  baseline: Record<string, unknown>
  components: Record<string, unknown>
}

/** Por qué una sesión tiene (o no) ciclos medidos — espejo de
 * `backend/app/routers/know_api.py::_signal_state`. `no_signal` (jugó sin
 * kit) NO es lo mismo que `processing` (bronze subido, silver pendiente):
 * sólo el segundo justifica un "volvé más tarde". */
export type SignalState = 'measured' | 'no_cycles' | 'processing' | 'no_signal'

export interface PhysiologySessionEntry {
  id: string
  started_at: string | null
  ended_at: string | null
  status: string
  silver_materialized_at: string | null
  state: SignalState
  /** Llegó el bronze físiológico del kit para esta sesión. */
  has_physio: boolean
  n_trials: number
  /** Alias histórico de `state === 'processing'`. */
  pending: boolean
  cycles: RegulationCycle[]
}

export function getMyPhysiologySessions(): Promise<{ sessions: PhysiologySessionEntry[] }> {
  return api.get<{ sessions: PhysiologySessionEntry[] }>('/know/me/physiology/sessions')
}
